import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import * as yup from "yup";

import DatePicker from "@components/elements/DatePicker";
import { PageConsultation } from "@components/layouts/PageConsultation";
import DocsModal from "@elements/DocsModal";
import ModalWindow from "@elements/ModalWindow";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuestionValidation } from "@hooks/questionnaire/useQuestionValidation";
import useAmplitude from "@hooks/useAmplitude";
import { useTitle } from "@hooks/useTitle";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {
  Button,
  Feedback,
  FormGroup,
  FormGroupMessage,
  Label,
  Link,
  Text,
  TextField,
  Title,
} from "@runwayhealth/runway-components-react";
import { RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";
import { buildParentalConsentPayload, formatPhoneNumber } from "@utils/questionnaire/parentalConsent";
import { validatePhone } from "@utils/forms/utilsFormvalidation";

import { ParentalConsentBody } from "./ParentalConsentBody";
import { ParentalConsentFooter } from "./ParentalConsentFooter";

export interface ParentalConsentData {
  firstName: string;
  lastName: string;
  fullname: string;
  email: string;
  phone: string;
  parentalConsent: boolean;
  terms: boolean;
  consent: boolean;
  birthdate: string;
}

const schema = yup
  .object()
  .shape({
    firstName: yup
      .string()
      .required("This field is required")
      .min(2, "Must contain at least 2 characters"),
    lastName: yup
      .string()
      .required("This field is required")
      .min(2, "Must contain at least 2 characters"),
    email: yup.string().email("Please enter a valid email").required("This field is required"),
    phone: yup
      .string()
      .test({
        test(value, ctx) {
          const formatted = formatPhoneNumber(value || "");

          if (!validatePhone(formatted)) {
            return ctx.createError({
              message: "Please enter a valid US phone number",
            });
          }

          return true;
        },
      })
      .min(10, "Must contain minimum 10 characters")
      .max(18, "Must contain maximum 18 characters")
      .required("This field is required"),
    parentalConsent: yup.boolean().oneOf([true], "This field is required"),
    terms: yup.boolean().oneOf([true], "This field is required"),
    consent: yup.boolean().oneOf([true], "This field is required"),
  })
  .required();

const defaultValues: ParentalConsentData = {
  firstName: "",
  lastName: "",
  fullname: "",
  email: "",
  phone: "",
  parentalConsent: false,
  terms: false,
  consent: false,
  birthdate: "",
};

type ParentalConsentProps = {
  onAnswer: (answer: ParentalConsentData) => void;
};

const ParentalConsent: React.FC<ParentalConsentProps> = ({ onAnswer }) => {
  const [error, setError] = useState("");
  const [modal, setModal] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [birthdate, setBirthdate] = useState<string | null>(null);
  const { getUsersAge } = useQuestionValidation();

  useTitle("Parental Consent");
  const { logEvent } = useAmplitude();
  const {
    data: { driver_license_id, driver_license_case },
  } = useSelector<RootState, RootEntity<User>>((state) => state.user);
  const activeCaseId = useSelector((state: RootState) => state.case.newCase.data.id);
  // Treat the ID as uploaded only when it belongs to the active case, so a prior
  // consultation's ID cannot leak into a new one (ZD-3226 isolation). An
  // in-progress upload for the current case still matches and shows (INV-1).
  const hasGuardianId =
    !!driver_license_id && !!activeCaseId && driver_license_case === String(activeCaseId);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<ParentalConsentData>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues,
  });

  const isDisabled = !isDirty || !isValid || !birthdate || !hasGuardianId;

  const onSubmit: SubmitHandler<ParentalConsentData> = (data) => {
    if (!birthdate || error.length > 0) {
      return;
    }

    if (!hasGuardianId) {
      setError("Please submit a valid legal guardian's photo ID.");
      return;
    }

    onAnswer(buildParentalConsentPayload(data, birthdate));
  };

  const handlePhotoUpload = () => {
    logEvent("ID_MODAL_OPEN");
    setModal(true);
  };

  useEffect(() => {
    logEvent("ID_START");
  }, []);

  // Validate birthdate field.
  useEffect(() => {
    setError("");
    if (birthdate) {
      const usersAge = getUsersAge(new Date(birthdate));

      if (usersAge < 18 || isNaN(usersAge)) {
        setError("The legal guardian must be at least 18 years old");
      }
    }
  }, [birthdate, hasGuardianId]);

  const handleOpener = (e: any) => {
    e.preventDefault();
    setShowLegal(true);
    setLegalName(e.currentTarget.id);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PageConsultation.Body>
          <ParentalConsentBody>
            <div className="row justify-content-center">
              <div className="col-md-6">
                <FormGroup>
                  <Label htmlFor="firstName">Legal guardian's first name</Label>
                  <TextField
                    id="firstName"
                    placeholder="First"
                    isInvalid={errors.firstName}
                    {...register("firstName")}
                  />
                  <FormGroupMessage>
                    {errors.firstName && (
                      <Feedback size="sm" isInvalid>
                        {errors.firstName?.message}
                      </Feedback>
                    )}
                  </FormGroupMessage>
                </FormGroup>
              </div>

              <div className="col-md-6">
                <FormGroup>
                  <Label htmlFor="lastName">Legal guardian's last name</Label>
                  <TextField
                    id="lastName"
                    placeholder="Last"
                    isInvalid={errors.lastName}
                    {...register("lastName")}
                  />
                  <FormGroupMessage>
                    {errors.lastName && (
                      <Feedback size="sm" isInvalid>
                        {errors.lastName?.message}
                      </Feedback>
                    )}
                  </FormGroupMessage>
                </FormGroup>
              </div>

              <div className="col-md-6">
                <FormGroup>
                  <Label htmlFor="email">Legal guardian's email</Label>
                  <TextField
                    id="email"
                    placeholder="Email"
                    type="email"
                    isInvalid={errors.email}
                    {...register("email")}
                  />
                  <FormGroupMessage>
                    {errors.email && (
                      <Feedback size="sm" isInvalid>
                        {errors.email?.message}
                      </Feedback>
                    )}
                  </FormGroupMessage>
                </FormGroup>
              </div>

              <div className="col-md-6">
                <FormGroup>
                  <Label htmlFor="phone">Legal guardian's phone</Label>
                  <TextField
                    id="phone"
                    placeholder="(999) 999-9999"
                    isInvalid={errors.phone}
                    {...register("phone")}
                    inputMode="numeric"
                  />
                  <FormGroupMessage>
                    {errors.phone && (
                      <Feedback size="sm" isInvalid>
                        {errors.phone?.message}
                      </Feedback>
                    )}
                  </FormGroupMessage>
                </FormGroup>
              </div>
            </div>

            <FormGroup className="input-birthday">
              <Label htmlFor="birthdate">Legal guardian's date of birth</Label>
              <DatePicker
                id="birthdate"
                value={birthdate ?? ""}
                onChange={(e) => setBirthdate(e.target.value)}
              />
              <FormGroupMessage>
                {error && birthdate && (
                  <Feedback size="sm" isInvalid>
                    {error}
                  </Feedback>
                )}
              </FormGroupMessage>
            </FormGroup>

            <FormGroup className="upload-image">
              {hasGuardianId ? (
                <Feedback isValid size="md">
                  Photo uploaded successfully.{" "}
                  <Button type="button" size="sm" variant="outline" onClick={handlePhotoUpload}>
                    Upload again
                  </Button>
                </Feedback>
              ) : (
                <>
                  <Label>Submit a photo of your legal guardian's ID</Label>
                  <Button
                    type="button"
                    size="md"
                    variant="outline"
                    iconName="Paperclip"
                    onClick={handlePhotoUpload}
                    isIconOnly
                  />
                </>
              )}
              <FormGroupMessage>
                {error && !hasGuardianId && (
                  <Feedback size="sm" isInvalid>
                    {error}
                  </Feedback>
                )}
              </FormGroupMessage>
            </FormGroup>

            <div className="legal-options">
              <FormControlLabel
                control={
                  <Controller
                    name="parentalConsent"
                    control={control}
                    render={({ field }) => (
                      <Checkbox color="default" checked={field.value} onChange={field.onChange} />
                    )}
                  />
                }
                label={
                  <Text as="span" size="sm" noMarginBottom>
                    I confirm that I have either directly provided this medical history or been
                    present to review my child's health information for this telehealth visit.
                  </Text>
                }
              />
              <FormGroupMessage className="rw-checkbox-feedback">
                {errors.parentalConsent && (
                  <Feedback size="sm" isInvalid>
                    {errors.parentalConsent?.message}
                  </Feedback>
                )}
              </FormGroupMessage>

              <FormControlLabel
                control={
                  <Controller
                    name="terms"
                    control={control}
                    render={({ field }) => (
                      <Checkbox color="default" checked={field.value} onChange={field.onChange} />
                    )}
                  />
                }
                label={
                  <Text as="span" size="sm" noMarginBottom>
                    I agree to Runway's{" "}
                    <Link as="span" id="terms" onClick={(e) => handleOpener(e)}>
                      Terms and Conditions
                    </Link>
                    ,{" "}
                    <Link as="span" id="privacy" onClick={(e) => handleOpener(e)}>
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link as="span" id="information" onClick={(e) => handleOpener(e)}>
                      Information Sharing
                    </Link>
                  </Text>
                }
              />
              <FormGroupMessage className="rw-checkbox-feedback">
                {errors.terms && (
                  <Feedback size="sm" isInvalid>
                    {errors.terms?.message}
                  </Feedback>
                )}
              </FormGroupMessage>

              <FormControlLabel
                control={
                  <Controller
                    name="consent"
                    control={control}
                    render={({ field }) => (
                      <Checkbox color="default" checked={field.value} onChange={field.onChange} />
                    )}
                  />
                }
                label={
                  <Text as="span" size="sm">
                    I agree to Runway's{" "}
                    <Link as="span" id="consent" onClick={(e) => handleOpener(e)}>
                      Telehealth Consent
                    </Link>
                  </Text>
                }
              />
              <FormGroupMessage className="rw-checkbox-feedback">
                {errors.consent && (
                  <Feedback size="sm" isInvalid>
                    {errors.consent?.message}
                  </Feedback>
                )}
              </FormGroupMessage>

              {showLegal && <DocsModal open={showLegal} close={setShowLegal} name={legalName} />}
            </div>
          </ParentalConsentBody>
        </PageConsultation.Body>

        <PageConsultation.Footer>
          <ParentalConsentFooter>
            <Button size="lg" iconName="ArrowRight" type="submit" disabled={isDisabled}>
              Continue
            </Button>
            <div className="row justify-content-center">
              <div className="col-md-8">
                <Text as="p" size="sm">
                  * Your data will be managed in compliance with HIPAA privacy regulations.
                </Text>
              </div>
            </div>
          </ParentalConsentFooter>
        </PageConsultation.Footer>
      </form>
      {modal && <ModalWindow open={modal} close={setModal} next={false} minor={true} />}
    </>
  );
};

export default ParentalConsent;
